import { BaseService } from "../baseService";
import { FileAPIService } from "../fileAPIService";
import { WarehouseCheckInAPIService } from "./warehouseCheckInAPIService";
import { IdGenerator, sanitiseParams } from "src/utils";
import { hasExpiryDateTotalMismatch, hasDuplicateExpiryDates } from "src/helpers";
import { ValidationError } from "src/errors";
import { NonOrderProductCheckIn, OrderProductCheckIn } from "src/types/database/warehouseCheckIn";
import {
  PostWarehouseCheckInPurchaseOrderRequestBody,
  PostWarehouseCheckInUnknownArrivalRequestBody
} from "src/types/api";
import { ISOString, GetWarehouseCheckInsQueryParams } from "src/types";

class WarehouseCheckInService extends BaseService<WarehouseCheckInAPIService> {
  private readonly fileAPIService: FileAPIService;

  constructor(apiService: WarehouseCheckInAPIService, fileAPIService: FileAPIService) {
    super(apiService);
    this.fileAPIService = fileAPIService;
  }

  @sanitiseParams()
  async getWarehouseCheckInById(warehouseCheckInId: string, timestamp: ISOString) {
    const warehouseCheckIn = await this.apiService.getWarehouseCheckInById(warehouseCheckInId, timestamp);

    return warehouseCheckIn;
  }

  @sanitiseParams()
  async updateWarehouseCheckInOpsNote(id: string, timestamp: ISOString, opsNote: string | null) {
    const updatedItem = await this.apiService.patchWarehouseCheckInOpsNote(id, timestamp, { opsNote });

    return updatedItem;
  }

  private validateProductCheckInsExpiryDates<T extends OrderProductCheckIn | NonOrderProductCheckIn>(
    productCheckIns: T[],
    formDataKey: "orderProductCheckIns" | "nonOrderProductCheckIns"
  ) {
    const invalidCheckIn = productCheckIns.find(
      (checkIn) =>
        hasExpiryDateTotalMismatch(checkIn.expiryDates, checkIn.receivedQty) ||
        hasDuplicateExpiryDates(checkIn.expiryDates ?? [])
    );

    if (invalidCheckIn && hasExpiryDateTotalMismatch(invalidCheckIn.expiryDates, invalidCheckIn.receivedQty)) {
      const expiryDateTotalMismatchMessage =
        "The total quantity of expiry dates for product check-in does not match the total quantity received";
      const invalidProductIdPrefix =
        "productId" in invalidCheckIn && invalidCheckIn.productId ? `(${invalidCheckIn.productId}) ` : "";
      throw new ValidationError(`${invalidProductIdPrefix}${expiryDateTotalMismatchMessage}`, formDataKey);
    }

    if (invalidCheckIn && hasDuplicateExpiryDates(invalidCheckIn.expiryDates ?? [])) {
      const duplicateExpiryDatesMessage = "Product check-in has duplicate expiry dates";
      const invalidProductIdPrefix =
        "productId" in invalidCheckIn && invalidCheckIn.productId ? `(${invalidCheckIn.productId}) ` : "";
      throw new ValidationError(`${invalidProductIdPrefix}${duplicateExpiryDatesMessage}`, formDataKey);
    }
  }

  @sanitiseParams()
  async getWarehouseCheckInImages(warehouseCheckInId: string, timestamp: string): Promise<File[]> {
    const warehouseCheckInImageFiles: File[] = [];

    const signedGETUrls = await this.apiService.postWarehouseCheckInImagesUrls(warehouseCheckInId, {
      timestamp: timestamp,
      operation: "GET"
    });

    for (const { signedUrl, fileName } of signedGETUrls) {
      const fileBlob = await this.fileAPIService.getFile(signedUrl);
      const warehouseCheckInImageFile = new File([fileBlob], fileName, {
        type: fileBlob.type,
        lastModified: new Date().getTime()
      });

      warehouseCheckInImageFiles.push(warehouseCheckInImageFile);
    }

    return warehouseCheckInImageFiles;
  }

  private async handleImageFiles(warehouseCheckInId: string, timestamp: string, checkInImages: File[]): Promise<void> {
    const checkInImagesNames = checkInImages.map(({ name }) => name);
    const signedPutUrls = await this.apiService.postWarehouseCheckInImagesUrls(warehouseCheckInId, {
      timestamp: timestamp,
      operation: "PUT",
      fileNames: checkInImagesNames
    });

    for (const { signedUrl, fileName } of signedPutUrls) {
      const file = checkInImages.find((imageFile) => imageFile.name === fileName);

      if (file) {
        await this.fileAPIService.putFile(file, signedUrl);
      }
    }
  }

  @sanitiseParams()
  async checkInPurchaseOrder(
    purchaseOrderId: string,
    checkInPurchaseOrderRequestBody: PostWarehouseCheckInPurchaseOrderRequestBody,
    checkInImages?: File[]
  ) {
    const { timestamp, extraProductCheckIns, orderProductCheckIns } = checkInPurchaseOrderRequestBody;

    this.validateProductCheckInsExpiryDates(orderProductCheckIns, "orderProductCheckIns");

    if (extraProductCheckIns) {
      this.validateProductCheckInsExpiryDates(extraProductCheckIns, "nonOrderProductCheckIns");
    }

    if (
      extraProductCheckIns &&
      extraProductCheckIns.some((extraProductCheckIn) =>
        orderProductCheckIns.some(
          (orderProductCheckIn) => orderProductCheckIn.productId === extraProductCheckIn.productId
        )
      )
    ) {
      throw new ValidationError(
        `Product check-in ID found in product check-ins; it's not an extra product`,
        "nonOrderProductCheckIns"
      );
    }

    const extraProductCheckInIds = extraProductCheckIns?.map(({ productId }) => productId).filter(Boolean);

    if (extraProductCheckIns && new Set(extraProductCheckInIds).size !== extraProductCheckInIds?.length) {
      throw new ValidationError("Products cannot have the same ID", "nonOrderProductCheckIns");
    }

    if (checkInImages?.length) {
      await this.handleImageFiles(purchaseOrderId, timestamp, checkInImages);
    }

    const warehouseCheckIn = await this.apiService.postWarehouseCheckInPurchaseOrder(
      purchaseOrderId,
      checkInPurchaseOrderRequestBody
    );

    return warehouseCheckIn;
  }

  @sanitiseParams()
  async checkInUnknownArrival(requestBody: PostWarehouseCheckInUnknownArrivalRequestBody, checkInImages?: File[]) {
    const { timestamp, productCheckIns } = requestBody;

    if (productCheckIns.some((productCheckIn) => !productCheckIn.productName && !productCheckIn.productId)) {
      throw new ValidationError(
        "Product check-ins must have a least a product ID or product name",
        "nonOrderProductCheckIns"
      );
    }

    if (productCheckIns) {
      this.validateProductCheckInsExpiryDates(productCheckIns, "nonOrderProductCheckIns");
    }

    const productCheckInIds = productCheckIns.map(({ productId }) => productId).filter(Boolean);

    if (new Set(productCheckInIds).size !== productCheckInIds.length) {
      throw new ValidationError("Product check-ins cannot have the same ID", "nonOrderProductCheckIns");
    }

    const warehouseCheckInId = "UNK-" + IdGenerator.generate6DigitId();

    if (checkInImages?.length) {
      await this.handleImageFiles(warehouseCheckInId, timestamp, checkInImages);
    }

    const warehouseCheckIn = await this.apiService.postWarehouseCheckInUnknownArrival(warehouseCheckInId, requestBody);

    return warehouseCheckIn;
  }

  @sanitiseParams()
  async getWarehouseCheckIns(queryParams: GetWarehouseCheckInsQueryParams) {
    const paginatedWarehouseCheckIns = await this.apiService.getWarehouseCheckIns(queryParams);

    return paginatedWarehouseCheckIns;
  }

  @sanitiseParams()
  async getGoodsInExportUrl(exportFileId: string) {
    const { signedUrl, fileName } = await this.apiService.postWarehouseCheckInsGoodsInExportUrl(exportFileId);

    const fileBlob = await this.fileAPIService.getFile(signedUrl);

    return new File([fileBlob], fileName, { type: fileBlob.type, lastModified: new Date().getTime() });
  }
}

export { WarehouseCheckInService };
